import React from 'react'
import { styled } from 'styled-components'
import { useRecoilValue } from 'recoil'
import { subDataState } from '@/util/states'
import { useRouter } from 'next/router'

import { useApiCall } from 'src/hooks/useApiCall'
import { DropCard } from './DropCard'
import { PostCard } from './PostCard'
import { Drop } from './Drop'

let STANDARD = '5U3SNl2Kab22pPlQVlTL7u'
let BEYOND = '7IQbJLyA8k9Y5RtOXKalQI'

export const Dashboard = () => {
  const router = useRouter()
  const subData = useRecoilValue(subDataState)
  const tier = subData.tier === 'Standard' ? STANDARD : BEYOND

  const drops = useApiCall({
    contentType: 'drop',
    order: '-fields.dropDate',
    limit: 5,
    options: { subscriptionTier: tier }
  })

  const posts = useApiCall({
    contentType: 'blogPost',
    order: '-fields.datePublished',
    limit: 3,
    options: { subscriptionRequired: false }
  })

  const firstFourDrops = drops.slice(0, 4)
  const fifthDrop = drops[4]

  return (
    <PageWrapper>
      <Section>
        <SectionHeader>
          <SectionTitle>Latest Drops</SectionTitle>
          <SeeAll onClick={() => router.push('/issues')}>See all</SeeAll>
        </SectionHeader>
        <DropList drops={firstFourDrops} />
      </Section>

      {posts.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitle>Editorials</SectionTitle>
            <SeeAll onClick={() => router.push('/blogs')}>See all</SeeAll>
          </SectionHeader>
          <PostList posts={posts} />
        </Section>
      )}
      
      <SecondaryRow>
        {fifthDrop && <Drop drop={fifthDrop} />}
        <MemberContentCard onClick={() => router.push('/issues')}>
          <CardContent>
            <TierIcons>
              <TierIcon src="/images/standard-icon.png" alt="Standard" />
              <TierIcon src="/images/beyond-icon.png" alt="Beyond" />
            </TierIcons>
            <CardTitle>View Your Member Content</CardTitle>
            <CardSubtitle>Access all your exclusive drops & issues</CardSubtitle>
            <ViewButton>View Content →</ViewButton>
          </CardContent>
        </MemberContentCard>
      </SecondaryRow>
    </PageWrapper>
  )
}

const DropList = ({ drops }) => (
  <DropGrid>
    {drops.map((d, index) => (
      <DropCard key={d.sys.id} drop={d} isNew={index === 0} />
    ))}
  </DropGrid>
)

const PostList = ({ posts }) => (
  <PostGrid>
    {posts.map((post) => (
      <PostCard key={post?.id} post={post} />
    ))}
  </PostGrid>
)

const PageWrapper = styled.div`
  padding: 20px 0;
  background: white;
  min-height: 100vh;
`

const Section = styled.section`
  margin-bottom: 40px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0 60px;
  max-width: 1300px;
  margin: 0 auto 16px;
  
  @media only screen and (max-width: 800px) {
    padding: 0 30px;
  }
  
  @media only screen and (max-width: 500px) {
    padding: 0 20px;
  }
`

const SectionTitle = styled.h2`
  color: #1a1a1a;
  font-size: 1.75rem;
  font-family: 'Soehne-Leicht', sans-serif;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.02em;
`

const SeeAll = styled.span`
  color: #666;
  font-size: 0.95rem;
  font-family: 'Soehne-Leicht', sans-serif;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: #1a1a1a;
  }
`

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  justify-content: center;
  @media only screen and (max-width: 600px) {
    flex-direction: column;
  }
`

const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 0 60px;
  max-width: 1300px;
  margin: 0 auto;
  
  @media only screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 30px;
  }
  
  @media only screen and (max-width: 550px) {
    grid-template-columns: 1fr;
    padding: 0 20px;
  }
`

const DropGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 0 60px;
  max-width: 1300px;
  margin: 0 auto;
  
  @media only screen and (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media only screen and (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 30px;
  }
  
  @media only screen and (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 20px;
    gap: 12px;
  }
`

const SecondaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 20px 60px 40px;
  max-width: 1300px;
  margin: 0 auto;
  
  & > *:first-child {
    width: 100% !important;
  }
  
  @media only screen and (max-width: 800px) {
    grid-template-columns: 1fr;
    padding: 20px 30px 40px;
  }
  
  @media only screen and (max-width: 500px) {
    padding: 20px 20px 40px;
  }
`

const MemberContentCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 8px;
  border: 8px #f0f0f0 solid;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.01);
  }
`

const CardContent = styled.div`
  text-align: center;
  padding: 40px 30px;
`

const TierIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
`

const TierIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
`

const CardTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-family: 'Soehne-Leicht', sans-serif;
  margin: 0 0 10px 0;
  font-weight: 400;
`

const CardSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  font-family: 'Soehne-Leicht', sans-serif;
  margin: 0 0 28px 0;
`

const ViewButton = styled.span`
  display: inline-block;
  background: white;
  color: #1a1a1a;
  padding: 14px 28px;
  border-radius: 6px;
  font-size: 1rem;
  font-family: 'Soehne-Leicht', sans-serif;
  font-weight: 500;
  transition: background 0.2s ease;
  
  ${MemberContentCard}:hover & {
    background: #f0f0f0;
  }
`
const Highlight = styled.span`
  background: #d2fc33;
`

const Vol2Cover = styled.img`
  width: 80%;
  display: block;
  margin: 0 auto;
`
